import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { orderOfferVasService } from '@search/offer-placement-shared/src';
import { BuyableVasGqlEnum } from '@search/graphql-typings';
import { useGqlContext } from '@search/gql-client/src/gqlContext';
import { useSelectedVasPricesData, useVasPricesData } from '@search/offer-placement-shared/src/services/vasPrices';
import { VasPeriod } from '@search/offer-placement-shared/src/enums';
import { VasData } from '@search/offer-placement-shared/src/types/vas';

import { useClassifiedOfferDataLayerPush } from '../../../../domain/offer/analytics/ClassifiedOfferGtmContext';

type UseOfferCardVasBlock = {
    offerId: string;
    regionId: number;
    userPhone?: string;
    gtmChoiceOption?: string;
}

export type VasPriceOption = {
    value: VasPeriod;
    actualPrice: number;
    label: string;
    days: number;
    discount: number;
    oldPrice?: number;
}

function convertVasData(data: VasData): VasPriceOption {
    return ({
        value: data.period,
        actualPrice: Math.ceil((data.totalPrice ?? 0) / 100),
        days: data.days,
        label: `${data.days} дней`,
        discount: Math.ceil(data.discount ?? 0),
        oldPrice: (data.discount && data.totalPriceBeforeDiscount) ?
            Math.ceil(data.totalPriceBeforeDiscount / 100) : undefined
    });
}

const availableTypes = [ BuyableVasGqlEnum.Top ];

export function useOfferCardVasBlock({
    userPhone = '',
    offerId,
    regionId,
    gtmChoiceOption = 'cl-card-offer-promote-price-choice'
}: UseOfferCardVasBlock) {
    const gql = useGqlContext();
    const offersParams = useMemo(() => [ { offerId, regionId } ], [ offerId, regionId ]);
    const [ vasPeriod, setVasPeriod ] = useState(VasPeriod.MONTH);
    const isSelected = useRef(false);
    const { vasDataMap } = useVasPricesData({ gql, offersParams });
    const { selectedVasData, vasData } = useSelectedVasPricesData({ vasDataMap, vas: {
        period: vasPeriod,
        types: availableTypes
    } });

    const vasPriceOption = useMemo<VasPriceOption | undefined>(() => {
        if (selectedVasData !== undefined) {
            return convertVasData(selectedVasData);
        }
        return undefined;
    }, [ selectedVasData ]);

    const vasPriceOptions = useMemo<VasPriceOption[]>(() => {
        return vasData.map(vasD => convertVasData(vasD));
    }, [ vasData ]);

    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const setVasPeriodValue = useCallback((periodKey: VasPeriod) => {
        isSelected.current = true;

        setVasPeriod(periodKey);
    });

    useEffect(() => {
        if (vasPriceOption !== undefined && isSelected.current) {
            gtmChoiceOption && dataLayerPush({
                offerId,
                event: gtmChoiceOption,
                offerCost: vasPriceOption.actualPrice
            });
        }
    }, [ vasPriceOption ]);

    const handleBuyVas = useCallback(() => {
        return new Promise((_resolve, reject) => {
            orderOfferVasService({
                gql,
                offersParams,
                vases: availableTypes,
                durationDays: vasPeriod,
                phone: userPhone
            }).then(({ redirectUrl }) => {
                if (redirectUrl) {
                    dataLayerPush({
                        offerId,
                        event: 'cl-serp-offer-promote-click-pay',
                        offerCost: vasPriceOption?.actualPrice
                    });
                    window.location.href = redirectUrl;
                } else {
                    reject('redirectUrl is not defined');
                }
            }).catch(e => reject(e));
        });
    }, [ gql, offersParams, vasPeriod, offerId, vasPriceOption, userPhone ]);

    return {
        vasPriceOption,
        vasPriceOptions,
        setVasPeriodValue,
        handleBuyVas
    };
}
